import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import Modal from "../../../components/modal/Modal";
import { ProjectPhase } from "../../../types/api";
import { useProjectPhaseStore, useProjectUtilityStore } from "../../../store/utilitySystemsStore";

interface ProjectPhaseFormProps {
  phase?: ProjectPhase | null;
  mode: "create" | "edit" | "view";
  onClose: () => void;
  onSuccess?: () => void;
}

const ProjectPhaseForm: React.FC<ProjectPhaseFormProps> = ({
  phase,
  mode,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    projectId: "",
    name: "",
    estimatedCost: 0,
    actualCost: 0,
    threshold: 1.1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { projects, fetchProjects } = useProjectUtilityStore();
  const {
    phasesLoading,
    fetchPhaseById,
    createPhase,
    updatePhase,
  } = useProjectPhaseStore();

  const isDisabled = mode === "view";
  const isReadOnly = mode === "view";

  const loadPhaseData = useCallback(async () => {
    if (!phase) return;

    try {
      const id = phase.id || phase._id;
      if (!id) return;

      const data = await fetchPhaseById(id);
      if (data) {
        setFormData({
          projectId: data.projectId || "",
          name: data.name || "",
          estimatedCost: data.estimatedCost || 0,
          actualCost: data.actualCost || 0,
          threshold: data.threshold || 1.1,
        });
      } else {
        setFormData({
          projectId: phase.projectId || "",
          name: phase.name || "",
          estimatedCost: phase.estimatedCost || 0,
          actualCost: phase.actualCost || 0,
          threshold: phase.threshold || 1.1,
        });
      }
    } catch (error) {
      console.error("Error loading phase:", error);
      toast.error("Failed to load phase data");
      if (phase) {
        setFormData({
          projectId: phase.projectId || "",
          name: phase.name || "",
          estimatedCost: phase.estimatedCost || 0,
          actualCost: phase.actualCost || 0,
          threshold: phase.threshold || 1.1,
        });
      }
    }
  }, [phase, fetchPhaseById]);

  useEffect(() => {
    if (!projects.length) fetchProjects();
    if (mode === "edit" || mode === "view") {
      loadPhaseData();
    } else {
      setFormData({
        projectId: "",
        name: "",
        estimatedCost: 0,
        actualCost: 0,
        threshold: 1.1,
      });
    }
    setErrors({});
  }, [mode, fetchProjects, projects.length, loadPhaseData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.projectId) {
      newErrors.projectId = "Project is required";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Phase Name is required";
    }

    if (formData.estimatedCost < 0) {
      newErrors.estimatedCost = "Estimated Cost must be >= 0";
    }

    if (formData.actualCost < 0) {
      newErrors.actualCost = "Actual Cost must be >= 0";
    }

    if (formData.threshold <= 0) {
      newErrors.threshold = "Threshold must be > 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (mode === "create") {
        await createPhase(formData);
        toast.success("Project phase created successfully");
      } else if (mode === "edit") {
        const id = phase?.id || phase?._id;
        if (!id) {
          toast.error("Phase ID is required");
          return;
        }
        await updatePhase(id, formData);
        toast.success("Project phase updated successfully");
      }
      onSuccess?.();
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || "Failed to save project phase"
      );
      console.error("Error saving phase:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const variance = formData.actualCost - formData.estimatedCost;
  const variancePercent =
    formData.estimatedCost > 0
      ? ((variance / formData.estimatedCost) * 100).toFixed(1)
      : "0";
  const thresholdAmount = formData.estimatedCost * formData.threshold;
  const isOverThreshold = formData.actualCost > thresholdAmount;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      modalClass="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl relative max-h-[80vh] flex flex-col"
    >
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          {mode === "create" ? "Create Project Phase" : mode === "edit" ? "Edit Project Phase" : "View Project Phase"}
        </h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.projectId}
              onChange={(e) =>
                setFormData({ ...formData, projectId: e.target.value })
              }
              disabled={isDisabled}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.projectId ? "border-red-500" : "border-gray-300"
              } ${isDisabled ? "bg-gray-100" : ""}`}
            >
              <option value="">Select Project</option>
              {projects.map((project) => {
                const id = project.id || project._id;
                return (
                  <option key={id} value={id}>
                    {project.name}
                  </option>
                );
              })}
            </select>
            {errors.projectId && (
              <p className="text-red-500 text-xs mt-1">{errors.projectId}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phase Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              disabled={isDisabled}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.name ? "border-red-500" : "border-gray-300"
              } ${isDisabled ? "bg-gray-100" : ""}`}
              placeholder="e.g., HVAC Setup, Civil Works"
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Cost (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.estimatedCost}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  estimatedCost: parseFloat(e.target.value) || 0,
                })
              }
              disabled={isDisabled}
              min="0"
              step="0.01"
              className={`w-full px-3 py-2 border rounded-md ${
                errors.estimatedCost ? "border-red-500" : "border-gray-300"
              } ${isDisabled ? "bg-gray-100" : ""}`}
            />
            {errors.estimatedCost && (
              <p className="text-red-500 text-xs mt-1">
                {errors.estimatedCost}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Actual Cost (₹)
            </label>
            <input
              type="number"
              value={formData.actualCost}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  actualCost: parseFloat(e.target.value) || 0,
                })
              }
              disabled={isDisabled}
              min="0"
              step="0.01"
              className={`w-full px-3 py-2 border rounded-md ${
                errors.actualCost ? "border-red-500" : "border-gray-300"
              } ${isDisabled ? "bg-gray-100" : ""}`}
            />
            {errors.actualCost && (
              <p className="text-red-500 text-xs mt-1">{errors.actualCost}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Budget Threshold (multiplier)
            </label>
            <input
              type="number"
              value={formData.threshold}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  threshold: parseFloat(e.target.value) || 1.1,
                })
              }
              disabled={isDisabled}
              min="0.01"
              step="0.01"
              className={`w-full px-3 py-2 border rounded-md ${
                errors.threshold ? "border-red-500" : "border-gray-300"
              } ${isDisabled ? "bg-gray-100" : ""}`}
              placeholder="1.1 (110%)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Alert threshold: {formatCurrency(thresholdAmount)} (
              {(formData.threshold * 100).toFixed(0)}% of estimated)
            </p>
            {errors.threshold && (
              <p className="text-red-500 text-xs mt-1">{errors.threshold}</p>
            )}
          </div>
        </div>

        {/* Budget Summary */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h3 className="font-semibold text-gray-700 mb-3">Budget Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-600">Estimated</p>
              <p className="font-semibold text-gray-800">
                {formatCurrency(formData.estimatedCost)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Actual</p>
              <p className="font-semibold text-gray-800">
                {formatCurrency(formData.actualCost)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Variance</p>
              <p
                className={`font-semibold ${
                  variance > 0
                    ? "text-red-600"
                    : variance < 0
                    ? "text-green-600"
                    : "text-gray-800"
                }`}
              >
                {formatCurrency(variance)} ({variancePercent}%)
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Status</p>
              <p
                className={`font-semibold ${
                  isOverThreshold
                    ? "text-red-600"
                    : variance > 0
                    ? "text-orange-600"
                    : variance < 0
                    ? "text-green-600"
                    : "text-blue-600"
                }`}
              >
                {isOverThreshold
                  ? "Over Threshold"
                  : variance > 0
                  ? "Over Budget"
                  : variance < 0
                  ? "Under Budget"
                  : "On Track"}
              </p>
            </div>
          </div>
        </div>

        {!isReadOnly && (
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={phasesLoading}
              className="px-4 py-2 bg-[#0fa9a6] text-white rounded-md hover:bg-[#0d8f8c] disabled:opacity-50"
            >
              {phasesLoading
                ? "Saving..."
                : mode === "create"
                ? "Create Phase"
                : "Update Phase"}
            </button>
          </div>
        )}
        {isReadOnly && (
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-[#0fa9a6] text-white rounded-md hover:bg-[#0d8f8c]"
            >
              Close
            </button>
          </div>
        )}
      </form>
      </div>
    </Modal>
  );
};

export default ProjectPhaseForm;

